/**
 * Accuracy checklist — the show-of-work panel. Live during the edit
 * theater (rows check off as the acts perform) and persisted on the
 * finished message as a collapsed accordion the reader can reopen.
 * Skipped steps keep the empty radio circle and strike through.
 */

import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@components/icons';

export interface ChecklistRow {
  act: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'skipped';
}

interface AccuracyChecklistProps {
  title: string;
  acts: ChecklistRow[];
  collapsed: boolean;
  onToggle: () => void;
  /** data-testid for the title span (the live theater status hook). */
  statusTestId?: string;
  /** Extra head content (e.g. the live show's Skip button). */
  trailing?: ReactNode;
}

export function AccuracyChecklist({
  title,
  acts,
  collapsed,
  onToggle,
  statusTestId,
  trailing,
}: AccuracyChecklistProps) {
  return (
    <div
      className={`accuracy-checklist${collapsed ? ' accuracy-checklist--collapsed' : ''}`}
      data-testid="accuracy-checklist"
    >
      <div className="accuracy-checklist-head">
        <button
          type="button"
          className="accuracy-checklist-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
          title={collapsed ? 'Show accuracy checks' : 'Hide accuracy checks'}
        >
          <ChevronRightIcon
            className={`accuracy-checklist-chevron h-3.5 w-3.5${collapsed ? '' : ' open'}`}
          />
          <span data-testid={statusTestId} className="edit-theater-status">
            {title}
          </span>
        </button>
        {trailing}
      </div>
      <div className="accuracy-checklist-body">
        {acts.map((a) => (
          <div
            key={a.act}
            data-testid="check-chip"
            className={`accuracy-check-row accuracy-check-row--${a.status}`}
          >
            <span className="accuracy-check-icon" aria-hidden="true">
              {a.status === 'done' ? '✓' : ''}
            </span>
            <span className="accuracy-check-label">{a.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
